import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Server, type SshKey } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, statusTone } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

export function ServersPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const servers = useQuery({
    queryKey: ['servers'],
    queryFn: () => api.get<{ servers: Server[] }>('/api/servers'),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Servers</h1>
          <p className="text-sm text-slate-500 mt-1">Orchestrator hosts that run tenant containers.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="self-start sm:self-auto shrink-0">
          <Plus size={14} /> Add server
        </Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {servers.isLoading ? (
            <div className="text-sm text-slate-500 p-5">Loading…</div>
          ) : !servers.data?.servers.length ? (
            <div className="p-8 text-center text-sm text-slate-500">
              No servers registered yet. Add one to begin onboarding tenants.
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="px-5 py-3">ID</th>
                  <th className="px-3 py-3">Region</th>
                  <th className="px-3 py-3">Hostname</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Health</th>
                  <th className="px-3 py-3">Load</th>
                  <th className="px-3 py-3">RAM</th>
                  <th className="px-3 py-3">Disk</th>
                  <th className="px-3 py-3">Tenants</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {servers.data.servers.map((server) => (
                  <tr key={server.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-3 font-mono text-xs">
                      <Link to={`/servers/${server.id}`} className="text-slate-900 hover:text-slate-700">
                        {server.id}
                      </Link>
                    </td>
                    <td className="px-3 py-3 text-slate-700">{server.region}</td>
                    <td className="px-3 py-3 text-slate-700 font-mono text-xs">{server.hostname}</td>
                    <td className="px-3 py-3">
                      <Badge tone={statusTone(server.status)}>{server.status}</Badge>
                    </td>
                    <td className="px-3 py-3">
                      {server.health_status ? (
                        <Badge tone={statusTone(server.health_status)}>{server.health_status}</Badge>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3"><LoadCell server={server} /></td>
                    <td className="px-3 py-3"><RamCell server={server} /></td>
                    <td className="px-3 py-3"><DiskCell server={server} /></td>
                    <td className="px-3 py-3 text-slate-700">
                      {server.tenant_count ?? 0} / {server.capacity}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Link
                        to={`/servers/${server.id}`}
                        className="text-xs text-slate-700 hover:text-slate-900 underline-offset-2 hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </CardBody>
      </Card>

      <AddServerDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}

function pctTone(pct: number): string {
  if (pct >= 90) return 'text-red-600';
  if (pct >= 75) return 'text-amber-600';
  return 'text-slate-700';
}

function LoadCell({ server }: { server: Server }) {
  const m = server.metrics_detail;
  if (!m?.load) return <span className="text-xs text-slate-400">—</span>;
  const cores = m.cpu_count ?? 1;
  const pct = (m.load['1m'] / cores) * 100;
  return (
    <span className={cn('text-xs font-mono tabular-nums', pctTone(pct))}>
      {m.load['1m'].toFixed(2)}
      <span className="text-slate-400"> / {cores}</span>
    </span>
  );
}

function RamCell({ server }: { server: Server }) {
  const m = server.metrics_detail?.memory;
  if (!m || m.total_kb === 0) return <span className="text-xs text-slate-400">—</span>;
  const pct = (m.used_kb / m.total_kb) * 100;
  return (
    <div className="w-24">
      <div className={cn('text-xs font-mono tabular-nums', pctTone(pct))}>{pct.toFixed(0)}%</div>
      <div className="mt-0.5 h-1 w-full rounded-full overflow-hidden bg-slate-100">
        <div
          className={cn(
            'h-full',
            pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500'
          )}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}

function DiskCell({ server }: { server: Server }) {
  const disks = server.metrics_detail?.disks ?? [];
  // Find the most-utilized mount — that's the operator's actionable signal.
  const worst = disks.reduce<typeof disks[number] | null>(
    (acc, d) => (acc == null || d.use_percent > acc.use_percent ? d : acc),
    null
  );
  if (!worst) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="w-24">
      <div className={cn('text-xs font-mono tabular-nums', pctTone(worst.use_percent))}>
        {worst.use_percent.toFixed(0)}%
        <span className="text-slate-400 ml-1 font-sans">{worst.mount}</span>
      </div>
      <div className="mt-0.5 h-1 w-full rounded-full overflow-hidden bg-slate-100">
        <div
          className={cn(
            'h-full',
            worst.use_percent >= 90
              ? 'bg-red-500'
              : worst.use_percent >= 75
              ? 'bg-amber-500'
              : 'bg-emerald-500'
          )}
          style={{ width: `${Math.min(100, worst.use_percent)}%` }}
        />
      </div>
    </div>
  );
}

function AddServerDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const sshKeys = useQuery({
    queryKey: ['ssh-keys'],
    queryFn: () => api.get<{ ssh_keys: SshKey[] }>('/api/ssh-keys'),
    enabled: open,
  });

  const [form, setForm] = useState({
    id: '',
    region: '',
    hostname: '',
    ssh_user: 'godmode',
    ssh_key_id: '',
    capacity: 100,
    public_ip: '',
    notes: '',
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/servers', {
        ...form,
        ssh_key_id: form.ssh_key_id || null,
        public_ip: form.public_ip.trim() || null,
        notes: form.notes || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['servers'] });
      onOpenChange(false);
      setForm({ id: '', region: '', hostname: '', ssh_user: 'godmode', ssh_key_id: '', capacity: 100, public_ip: '', notes: '' });
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add server"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add server'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="id">ID (slug)</Label>
            <Input id="id" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} placeholder="de1" />
          </div>
          <div>
            <Label htmlFor="region">Region</Label>
            <Input
              id="region"
              value={form.region}
              onChange={(e) => setForm({ ...form, region: e.target.value })}
              placeholder="de1"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="hostname">Hostname</Label>
          <Input
            id="hostname"
            value={form.hostname}
            onChange={(e) => setForm({ ...form, hostname: e.target.value })}
            placeholder="de1.wpgodmode.com"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="ssh_user">SSH user</Label>
            <Input
              id="ssh_user"
              value={form.ssh_user}
              onChange={(e) => setForm({ ...form, ssh_user: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="capacity">Capacity</Label>
            <Input
              id="capacity"
              type="number"
              min={1}
              value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value, 10) || 1 })}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="public_ip">Public IP (optional)</Label>
          <Input
            id="public_ip"
            value={form.public_ip}
            onChange={(e) => setForm({ ...form, public_ip: e.target.value })}
            placeholder="leave empty — learned from the host"
          />
          <p className="text-xs text-slate-500 mt-1">
            Only used when a website is moved between servers: the other host is told to accept the
            transfer from this address. Filled in from the host itself the first time — set it here
            only if this server sits behind NAT.
          </p>
        </div>
        <div>
          <Label htmlFor="ssh_key_id">SSH key</Label>
          <select
            id="ssh_key_id"
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            value={form.ssh_key_id}
            onChange={(e) => setForm({ ...form, ssh_key_id: e.target.value })}
          >
            <option value="">— (skip; cannot SSH yet)</option>
            {sshKeys.data?.ssh_keys.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label} ({k.public_fingerprint.slice(0, 24)}…)
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
      </div>
    </Dialog>
  );
}
